import { type InputHTMLAttributes, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { Loader2 } from "lucide-react";

import { cn } from "@/common/utils";
import { Input } from "./input";

export interface SearchResult {
  id: number;
  label: string;
  sublabel?: string;
  data?: unknown;
}

interface SearchComboboxProps {
  placeholder?: string;
  queryKey: string;
  searchFn: (query: string) => Promise<SearchResult[]>;
  onSelect?: (result: SearchResult) => void;
  value?: string;
  onInputChange?: (value: string) => void;
  className?: string;
  inputClassName?: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
}

export function SearchCombobox({
  placeholder,
  queryKey,
  searchFn,
  onSelect,
  value: externalValue,
  onInputChange,
  className,
  inputClassName,
  inputMode,
}: SearchComboboxProps) {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const controlled = externalValue !== undefined;
  const displayValue = controlled ? externalValue : inputValue;

  const debouncedQuery = useDebounce(displayValue, 300);

  const { data: results = [], isFetching } = useQuery({
    queryKey: [queryKey, debouncedQuery],
    queryFn: () => searchFn(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  const showDropdown = isOpen && results.length > 0;

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <Input
          placeholder={placeholder}
          value={displayValue}
          className={inputClassName}
          inputMode={inputMode}
          onChange={(event) => {
            if (!controlled) setInputValue(event.target.value);
            else onInputChange?.(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        />
        {isFetching && (
          <Loader2 className="text-muted-foreground absolute top-1/2 right-2 h-3 w-3 -translate-y-1/2 animate-spin" />
        )}
      </div>
      {showDropdown && (
        <ul className="border-border bg-popover absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border py-1 text-xs shadow-md">
          {results.map((result) => (
            <li
              key={result.id}
              className="hover:bg-accent flex cursor-pointer items-baseline gap-2 px-3 py-1.5"
              onMouseDown={() => {
                if (!controlled) setInputValue(result.label);
                setIsOpen(false);
                onSelect?.(result);
              }}
            >
              <span className="text-popover-foreground">{result.label}</span>
              {result.sublabel && <span className="text-muted-foreground">{result.sublabel}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
